import cx from 'classnames'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FrameConfiguration, FrameSizeRelativeNumbers } from '../types'

const DEFAULT_AUTOPLAY_SPEED = 2400
const DEFAULT_TRANSITION_SPEED = 1200

export type PhotoWithOrientation = {
  url: string
  isLandscape: boolean
}

type Slide =
  | { type: 'pair'; images: [string, string] }
  | { type: 'single'; image: string }
  | { type: 'centered'; image: string }

export default function CustomPhotosHeaderSlidingContent({
  photos,
  frameOffsets,
  cutoutImageClassName,
  cutoutImage,
  frameConfiguration,
  autoplaySpeed = DEFAULT_AUTOPLAY_SPEED,
  transitionSpeed = DEFAULT_TRANSITION_SPEED,
}: {
  photos: PhotoWithOrientation[]
  frameOffsets?: Array<{
    left: number
    top: number
    scale?: number
    rotate?: number
    zIndex?: number
  }>
  cutoutImageClassName?: string
  cutoutImage: string
  frameConfiguration: FrameConfiguration
  autoplaySpeed?: number
  transitionSpeed?: number
}) {
  const [boundingBox, setBoundingBox] = useState<DOMRect | null>(null)
  const [imageElement, setImageElement] = useState<HTMLImageElement | null>(null)
  const [currentSlide, setCurrentSlide] = useState(0)
  const [isTransitioning, setIsTransitioning] = useState(true)
  const isMountedRef = useRef(true)

  // Track mounted state
  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  // Create slides based on orientation:
  // - Landscape photos → full-width (single)
  // - Portrait photos → side-by-side (pair)
  // - Odd remaining portrait → centered with margins
  // Alternate: 1 landscape, then 2 portraits
  const slides = useMemo(() => {
    const landscapes = photos.filter(p => p.isLandscape)
    const portraits = photos.filter(p => !p.isLandscape)

    const result: Slide[] = []
    let lIdx = 0
    let pIdx = 0

    // Alternate: 1 landscape, then 2 portraits
    while (lIdx < landscapes.length || pIdx < portraits.length) {
      // Take 1 landscape if available
      if (lIdx < landscapes.length) {
        result.push({ type: 'single', image: landscapes[lIdx].url })
        lIdx++
      }
      // Take 2 portraits if available
      if (pIdx + 1 < portraits.length) {
        result.push({ type: 'pair', images: [portraits[pIdx].url, portraits[pIdx + 1].url] })
        pIdx += 2
      } else if (pIdx < portraits.length) {
        // Odd remaining portrait - centered with margins
        result.push({ type: 'centered', image: portraits[pIdx].url })
        pIdx++
      }
    }

    return result
  }, [photos])

  const totalSlides = slides.length

  // Track slides includes a clone of the first slide at the end for infinite loop
  const trackSlides = useMemo(() => {
    if (totalSlides <= 1) {
      return slides
    }
    return [...slides, slides[0]]
  }, [slides, totalSlides])

  const trackLength = trackSlides.length

  // Callback ref pattern for reliable element reference
  const setImageRef = useCallback((node: HTMLImageElement | null) => {
    setImageElement(node)
  }, [])

  // ResizeObserver for responsive bounding box updates
  useEffect(() => {
    if (!imageElement) {
      return undefined
    }

    const updateBoundingBox = () => {
      setBoundingBox(imageElement.getBoundingClientRect())
    }

    const resizeObserver = new ResizeObserver(() => {
      updateBoundingBox()
    })

    updateBoundingBox()
    resizeObserver.observe(imageElement)
    window.addEventListener('resize', updateBoundingBox)

    return () => {
      resizeObserver.disconnect()
      window.removeEventListener('resize', updateBoundingBox)
    }
  }, [imageElement])

  // Auto-advance timer
  useEffect(() => {
    if (totalSlides <= 1) {
      return undefined
    }

    const interval = setInterval(() => {
      setCurrentSlide(prev => prev + 1)
    }, autoplaySpeed)

    return () => clearInterval(interval)
  }, [totalSlides, autoplaySpeed])

  // Handle infinite loop: when reaching the clone, jump back to real first slide
  useEffect(() => {
    if (currentSlide === totalSlides && totalSlides > 1) {
      const timeout = setTimeout(() => {
        if (!isMountedRef.current) {
          return
        }
        setIsTransitioning(false)
        setCurrentSlide(0)
        // Re-enable transition after the instant jump
        requestAnimationFrame(() => {
          if (!isMountedRef.current) {
            return
          }
          requestAnimationFrame(() => {
            if (!isMountedRef.current) {
              return
            }
            setIsTransitioning(true)
          })
        })
      }, transitionSpeed)
      return () => clearTimeout(timeout)
    }
  }, [currentSlide, totalSlides, transitionSpeed])

  // Calculate absolute frame dimensions with Math.round() to avoid sub-pixel issues
  const getFrameSizeAbsolute = useCallback(
    (
      frameSizeRelativeNumbers: FrameSizeRelativeNumbers,
      frameOffset?: { left: number; top: number; scale?: number },
    ) => {
      if (!boundingBox) {
        return {
          width: 0,
          height: 0,
          top: 0,
          left: 0,
        }
      }

      const relativeHeight =
        (boundingBox.width / frameConfiguration.width) * frameConfiguration.height
      const scale = frameOffset?.scale || 1.0

      const rawWidth = ((boundingBox.width * frameSizeRelativeNumbers.width) / 100) * scale
      const rawHeight = ((relativeHeight * frameSizeRelativeNumbers.height) / 100) * scale
      const rawTop =
        (relativeHeight * (frameSizeRelativeNumbers.top + (frameOffset?.top || 0) * 100)) / 100
      const rawLeft =
        (boundingBox.width * (frameSizeRelativeNumbers.left + (frameOffset?.left || 0) * 100)) / 100

      return {
        width: Math.round(rawWidth),
        height: Math.round(rawHeight),
        top: Math.round(rawTop),
        left: Math.round(rawLeft),
      }
    },
    [boundingBox, frameConfiguration],
  )

  // Get the first frame's absolute dimensions for the slider container (memoized)
  const sliderDimensions = useMemo(() => {
    const frame = frameConfiguration.frames[0]
    const frameOffset = frameOffsets?.[0]
    return getFrameSizeAbsolute(frame, frameOffset)
  }, [frameConfiguration.frames, frameOffsets, getFrameSizeAbsolute])

  const renderSlide = (slide: Slide, slideIdx: number) => {
    if (slide.type === 'single') {
      return (
        <img
          src={slide.image}
          className='w-full h-full object-cover'
          data-clarity-mask='True'
          loading={slideIdx === 0 ? 'eager' : 'lazy'}
          decoding='async'
          alt=''
        />
      )
    }

    if (slide.type === 'centered') {
      return (
        <div className='flex justify-center h-full w-full'>
          <img
            src={slide.image}
            className='h-full object-cover'
            style={{ width: 'calc(50% - 1px)' }}
            data-clarity-mask='True'
            loading={slideIdx === 0 ? 'eager' : 'lazy'}
            decoding='async'
            alt=''
          />
        </div>
      )
    }

    return (
      <>
        <img
          src={slide.images[0]}
          className='h-full object-cover'
          style={{ width: 'calc(50% - 1px)' }}
          data-clarity-mask='True'
          loading={slideIdx === 0 ? 'eager' : 'lazy'}
          decoding='async'
          alt=''
        />
        <img
          src={slide.images[1]}
          className='h-full object-cover'
          style={{ width: 'calc(50% - 1px)' }}
          data-clarity-mask='True'
          loading={slideIdx === 0 ? 'eager' : 'lazy'}
          decoding='async'
          alt=''
        />
      </>
    )
  }

  return (
    <>
      {/* Slider container - absolutely positioned within the frame area */}
      {boundingBox && sliderDimensions.width > 0 && (
        <div
          className='absolute overflow-hidden'
          style={{
            top: sliderDimensions.top,
            left: sliderDimensions.left,
            width: sliderDimensions.width,
            height: sliderDimensions.height,
            zIndex: 0,
          }}
        >
          {/* Sliding track */}
          <div
            className='flex h-full'
            style={{
              width: `${trackLength * 100}%`,
              transform: `translateX(-${(currentSlide * 100) / trackLength}%)`,
              transition: isTransitioning ? `transform ${transitionSpeed}ms ease` : 'none',
            }}
          >
            {trackSlides.map((slide, slideIdx) => (
              <div
                key={slideIdx}
                className='flex gap-[2px] h-full'
                style={{ width: `${100 / trackLength}%` }}
              >
                {renderSlide(slide, slideIdx)}
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Cutout image - on top */}
      <img
        src={cutoutImage}
        ref={setImageRef}
        className={cx('relative z-10', cutoutImageClassName)}
        loading='eager'
        fetchPriority='high'
        height={frameConfiguration.height}
        style={{ aspectRatio: `${frameConfiguration.width}/${frameConfiguration.height}` }}
        onLoad={e => {
          setBoundingBox((e.target as HTMLImageElement).getBoundingClientRect())
        }}
        alt=''
      />
    </>
  )
}
